import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BugReportIcon from '@mui/icons-material/BugReport';
import HelpIcon from '@mui/icons-material/Help';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import UndoIcon from '@mui/icons-material/Undo';
import WarningIcon from '@mui/icons-material/Warning';

// Import API service for authenticated requests
import api, { envisFindingsApi } from '../../../services/api';
import { chainageApi } from '../../../services/api';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';

// Import useAuth context for user information
import { useAuth } from '../../../context/AuthContext';

import AuthenticatedImage from '../../common/AuthenticatedImage'; // Import the new component

const DefectValidationModal = ({
  open,
  onClose,
  selectedDefect,
  defectTypesList,
  currentDefectTypeId,
  onDefectTypeChange,
  rejectionReason,
  onRejectionReasonChange,
  loadingDefectTypes,
  onValidate,
  onReject,
  onRevertToDraft,
  formatDateTime,
  viewOnly = false,
  canRevert = false,
}) => {
  const theme = useTheme();
  const { user } = useAuth(); // Add useAuth hook to get current user
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // View toggle state (image or map)
  const [currentView, setCurrentView] = useState('image');
  
  // Map state
  const [mapInstance, setMapInstance] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapCleanedUp, setMapCleanedUp] = useState(false);
  const mapContainerRef = useRef(null);
  
  // Chainage segment state
  const [chainageSegmentData, setChainageSegmentData] = useState(null);
  const [chainageSegmentLayer, setChainageSegmentLayer] = useState(null);
  const [loadingChainageSegment, setLoadingChainageSegment] = useState(false);
  
  // Persist chainage segment data across view changes
  const chainageSegmentDataRef = useRef(null);
  
  // Burst findings state
  const [burstFindings, setBurstFindings] = useState([]);
  const [loadingBurstFindings, setLoadingBurstFindings] = useState(false);
  const [burstStatistics, setBurstStatistics] = useState(null);
  const [showBurstSection, setShowBurstSection] = useState(false);
  
  // State for "Correct Bound" feature
  const [nearbyChainageSegments, setNearbyChainageSegments] = useState([]);
  const [selectedChainageSegmentId, setSelectedChainageSegmentId] = useState('');
  const [loadingNearbySegments, setLoadingNearbySegments] = useState(false);
  
  // Full-size image viewer states
  const [fullSizeImageOpen, setFullSizeImageOpen] = useState(false);
  const [currentFullSizeIndex, setCurrentFullSizeIndex] = useState(0);

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Revert to draft dialog state
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  // Auto-assign preview state
  const [autoAssignPreview, setAutoAssignPreview] = useState(null);
  const [loadingAutoAssignPreview, setLoadingAutoAssignPreview] = useState(false);

  // Bounding box state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [boundingBox, setBoundingBox] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  
  // Blink animation state
  const [shouldBlink, setShouldBlink] = useState(false);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState({ scale: 1, translateX: 0, translateY: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [showZoomHint, setShowZoomHint] = useState(false);
  
  const imageRef = React.useRef(null);
  const imageContainerRef = React.useRef(null);

  // Helper to get a secure, relative URL for the AuthenticatedImage component
  const getSecureImageUrl = React.useCallback((imagePath) => {
    if (!imagePath || imagePath === 'no-image') return null;

    // The backend now provides a clean, web-relative path like 'uploads/...'
    // We just need to ensure it's formatted correctly for the AuthenticatedImage component.
    let normalizedPath = imagePath.replace(/\\/g, '/');
    
    // The path from the database is already in the correct format (e.g., "uploads/...").
    // No leading slash is needed for the S3 key.
    
    // The AuthenticatedImage component will handle adding the API host.
    // We only need to provide the path, e.g., '/uploads/...'
    return normalizedPath;
  }, []);

  // Use detailed data if available, otherwise fall back to rawData
  const imageData = detailedData || selectedDefect?.rawData || selectedDefect;

  // Memoize main image URL to prevent recalculation on every render
  const mainImageUrl = React.useMemo(() => {
    return getSecureImageUrl(imageData?.original_image_path);
  }, [imageData?.original_image_path, getSecureImageUrl]);

  // Memoize burst image URLs to prevent recalculation on every render
  const burstImageUrls = React.useMemo(() => {
    return burstFindings.map(finding => getSecureImageUrl(finding.image_path));
  }, [burstFindings, getSecureImageUrl]);

  React.useEffect(() => {
    if (open && selectedDefect) {
      // Reset zoom and view when the modal is opened for a new defect
      setZoom({ scale: 1, translateX: 0, translateY: 0 });
      setCurrentView('image');

      setImageLoading(true);
      setImageError(false);
      fetchDetailedData();
      fetchBurstFindings();
      fetchNearbyChainageSegments();

      // Fetch auto-assign preview if not in viewOnly mode
      if (!viewOnly) {
        fetchAutoAssignPreview();
      }
    }
  }, [open, selectedDefect, viewOnly]);

  // Fetch auto-assign preview
  const fetchAutoAssignPreview = async () => {
    if (!selectedDefect?.id) return;

    try {
      setLoadingAutoAssignPreview(true);
      const response = await envisFindingsApi.getAutoAssignPreview(selectedDefect.id);
      setAutoAssignPreview(response.data);
    } catch (error) {
      console.error('Error fetching auto-assign preview:', error);
      setAutoAssignPreview(null);
    } finally {
      setLoadingAutoAssignPreview(false);
    }
  };

  React.useEffect(() => {
    // Initialize bounding box coordinates when data is available
    const dataSource = detailedData || selectedDefect?.rawData || selectedDefect;
    
    if (dataSource && dataSource.x1 !== undefined) {
      setBoundingBox({
        x1: dataSource.x1,
        y1: dataSource.y1,
        x2: dataSource.x2,
        y2: dataSource.y2
      });
      
      // Set the initially selected chainage segment
      const initialSegmentId = dataSource?.matched_chainage_segment_id || '';
      setSelectedChainageSegmentId(initialSegmentId.toString());
    }
  }, [detailedData, selectedDefect]);

  // Trigger blink animation when image loads and bounding box is ready
  React.useEffect(() => {
    if (!imageLoading && !imageError && boundingBox.x1 !== undefined && open) {
      setShouldBlink(true);
      // Stop blinking after 3 cycles (3 seconds with 1s per cycle)
      const timer = setTimeout(() => {
        setShouldBlink(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [imageLoading, imageError, boundingBox, open]);

  React.useEffect(() => {
    if (isEditing && (isDragging || isResizing)) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isEditing, isDragging, isResizing, dragStart, resizeHandle]);

  // Mouse event handlers for panning
  React.useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handlePanMove);
      document.addEventListener('mouseup', handlePanEnd);
      
      return () => {
        document.removeEventListener('mousemove', handlePanMove);
        document.removeEventListener('mouseup', handlePanEnd);
      };
    }
  }, [isPanning, panStart]);

  // Auto-hide zoom hint after 3 seconds
  React.useEffect(() => {
    if (zoom.scale > 1 && !isEditing) {
      setShowZoomHint(true);
      const timer = setTimeout(() => {
        setShowZoomHint(false);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowZoomHint(false);
    }
  }, [zoom.scale > 1, isEditing]);

  const fetchBurstFindings = async () => {
    if (!selectedDefect) return;
    
    setLoadingBurstFindings(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [Burst Findings] Fetching for defect:', selectedDefect.id, 'Type:', selectedDefect.recordType);
      }
      
      const response = await envisFindingsApi.getBurstFindings(selectedDefect.id);
      const data = response.data;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 [Burst Findings] API Response:', data);
      }
      
              if (data.success && data.burst_findings?.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [Burst Findings] Found burst images:', data.burst_findings.length);
          
          // Log each burst finding with its image path and coordinates
          data.burst_findings.forEach((finding, index) => {
            console.log(`   🖼️ [${index + 1}/${data.burst_findings.length}] ID: ${finding.id}, Sequence: ${finding.burst_sequence_order}, Path: "${finding.image_path}"`);
            console.log(`   📍 [Burst Finding ${index}] Coordinates: x1=${finding.x1}, y1=${finding.y1}, x2=${finding.x2}, y2=${finding.y2}`);
            console.log(`   📝 [Burst Finding ${index}] Full object:`, finding);
          });
        }
        
        setBurstFindings(data.burst_findings);
        setBurstStatistics(data.burst_statistics);
        setShowBurstSection(true);
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ [Burst Findings] Successfully set ${data.burst_findings.length} burst findings in state`);
        }
              } else {
        setBurstFindings([]);
        setBurstStatistics(null);
        setShowBurstSection(false);
        if (process.env.NODE_ENV === 'development') {
          console.log('ℹ️ [Burst Findings] No burst findings found or API returned unsuccessful response');
        }
      }
    } catch (error) {
      console.error('❌ [Burst Findings] Error fetching burst findings:', error);
      setBurstFindings([]);
      setBurstStatistics(null);
      setShowBurstSection(false);
    } finally {
      setLoadingBurstFindings(false);
    }
  };

  const fetchDetailedData = async () => {
    if (!selectedDefect) return;
    
    setLoadingDetails(true);
    try {
      if (selectedDefect.recordType === 'confirmed_defect') {
        console.log('Fetching confirmed defect data for defect ID:', selectedDefect.defectId);
        
        // For confirmed defects, fetch from the defects endpoint (which should include coordinates)
        try {
          const response = await api.get(`/envis-findings/defects/${selectedDefect.defectId}`);
          const defectData = response.data;
          console.log('Defect data received:', defectData);
          
          // Defects should have coordinate data directly in the table
          if (defectData.x1 !== undefined) {
            console.log('Using coordinates directly from defect data');
            setDetailedData({
              ...defectData,
              original_image_path: defectData.image_path
            });
            
            // Fetch chainage segment for confirmed defects
            if (defectData.chainage_segment_id) {
              fetchChainageSegmentData(defectData.chainage_segment_id);
            }
            return;
          } else {
            console.log('Defect missing coordinates, falling back to original bounding box');
            // Fallback to original bounding box if coordinates are missing
            if (defectData.original_bounding_box_id) {
              const bboxResponse = await api.get(`/envis-findings/bounding-boxes/${defectData.original_bounding_box_id}`);
              const bboxData = bboxResponse.data;
              console.log('Original bounding box data received:', bboxData);
              
              const mergedData = {
                ...defectData,
                x1: bboxData.x1,
                y1: bboxData.y1,
                x2: bboxData.x2,
                y2: bboxData.y2,
                image_width: bboxData.image_width,
                image_height: bboxData.image_height,
                original_image_path: defectData.image_path || bboxData.original_image_path
              };
              console.log('Merged defect data with bounding box coordinates:', mergedData);
              setDetailedData(mergedData);
              
              // Fetch chainage segment from bounding box data
              if (bboxData.matched_chainage_segment_id) {
                fetchChainageSegmentData(bboxData.matched_chainage_segment_id);
              }
              return;
            }
          }
        } catch (defectError) {
          console.warn('Failed to fetch defect data, trying alternative approach:', defectError);
        }
        
        // Fallback: If defects API fails, try to get bounding box data directly using originalBoundingBoxId
        if (selectedDefect.originalBoundingBoxId) {
          console.log('Fallback: Fetching original bounding box directly from ID:', selectedDefect.originalBoundingBoxId);
          try {
            const bboxResponse = await api.get(`/envis-findings/bounding-boxes/${selectedDefect.originalBoundingBoxId}`);
            const bboxData = bboxResponse.data;
            console.log('Fallback: Original bounding box data received:', bboxData);
            
            // Use bounding box data with defect information from table
            const fallbackData = {
              ...bboxData,
              defect_type_name: selectedDefect.defectType,
              confidence_score: selectedDefect.confidence,
              image_path: selectedDefect.rawData?.original_image_path || bboxData.original_image_path,
              original_image_path: selectedDefect.rawData?.original_image_path || bboxData.original_image_path
            };
            console.log('Fallback: Using bounding box data:', fallbackData);
            setDetailedData(fallbackData);
            
            // Fetch chainage segment from fallback bounding box data
            if (bboxData.matched_chainage_segment_id) {
              fetchChainageSegmentData(bboxData.matched_chainage_segment_id);
            }
            return;
          } catch (bboxError) {
            console.error('Fallback failed:', bboxError);
          }
        }
        
        // Final fallback: use rawData
        console.warn('Using rawData as final fallback');
        setDetailedData(selectedDefect.rawData || selectedDefect);
        
      } else {
        // For pending findings, fetch detailed bounding box data
        const response = await api.get(`/envis-findings/bounding-boxes/${selectedDefect.id}`);
        const data = response.data;
        setDetailedData(data);
        
        // Fetch chainage segment for pending findings
        if (data.matched_chainage_segment_id) {
          fetchChainageSegmentData(data.matched_chainage_segment_id);
        }
      }
    } catch (error) {
      console.error('Error fetching detailed data:', error);
      setDetailedData(selectedDefect.rawData || selectedDefect);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchChainageSegmentData = async (chainageSegmentId) => {
    if (!chainageSegmentId) return;
    
    setLoadingChainageSegment(true);
    try {
      console.log('Fetching chainage segment data for ID:', chainageSegmentId);
      
      // Fetch chainage segment geometry as GeoJSON
      const response = await chainageApi.getChainageSegmentGeometry(chainageSegmentId);
      const data = response.data;
      
      console.log('Chainage segment data received:', data);
      console.log('Geometry data:', data.geometry);
      console.log('Map instance exists:', !!mapInstance);
      setChainageSegmentData(data);
      
      // Also store in ref for persistence across view changes
      chainageSegmentDataRef.current = data;
      
      // Add to map if map is already initialized
      if (mapInstance && data.geometry) {
        console.log('Adding chainage segment to existing map');
        addChainageSegmentToMap(data);
      } else {
        console.log('Map not ready, chainage segment will be added when map initializes');
      }
    } catch (error) {
      console.error('Error fetching chainage segment data:', error);
      // Don't show error to user, just log it - chainage segment is optional
    } finally {
      setLoadingChainageSegment(false);
    }
  };

  const fetchNearbyChainageSegments = async () => {
    if (!selectedDefect?.rawData) {
      console.log('[DefectValidationModal] No rawData available for fetchNearbyChainageSegments');
      return;
    }

    const { rawData } = selectedDefect;
    const latitude = rawData?.latitude;
    const longitude = rawData?.longitude;
    const networkId = rawData?.network_id;
    const currentSegmentId = rawData?.matched_chainage_segment_id;

    console.log('[DefectValidationModal] fetchNearbyChainageSegments params:', {
      latitude,
      longitude,
      networkId,
      currentSegmentId,
      hasRawData: !!rawData
    });

    if (!latitude || !longitude || !networkId || !currentSegmentId) {
      console.warn('[DefectValidationModal] Missing required parameters for nearby segments:', {
        hasLatitude: !!latitude,
        hasLongitude: !!longitude,
        hasNetworkId: !!networkId,
        hasCurrentSegmentId: !!currentSegmentId
      });
      setNearbyChainageSegments([]);
      return;
    }

    setLoadingNearbySegments(true);
    try {
      const params = {
        latitude,
        longitude,
        network_id: networkId,
        current_segment_id: currentSegmentId,
        limit: 5
      };
      console.log('[DefectValidationModal] Calling chainageApi.getNearby with params:', params);
      
      const response = await chainageApi.getNearby(params);
      console.log('[DefectValidationModal] getNearby response:', response);
      
      const nearbySegments = response.data || [];
      setNearbyChainageSegments(nearbySegments);
      
      console.log('[DefectValidationModal] Set nearby segments:', nearbySegments.length, 'segments found');
    } catch (error) {
      console.error('[DefectValidationModal] Error fetching nearby chainage segments:', error);
      console.error('[DefectValidationModal] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      setNearbyChainageSegments([]);
    } finally {
      setLoadingNearbySegments(false);
    }
  };

  const addChainageSegmentToMap = async (chainageData) => {
    if (!mapInstance || !chainageData?.geometry) {
      console.log('Cannot add chainage segment - missing map or geometry:', {
        hasMap: !!mapInstance,
        hasGeometry: !!chainageData?.geometry
      });
      return;
    }
    
    try {
      console.log('Adding chainage segment to map...', chainageData);
      
      // Dynamically import Leaflet
      const L = await import('leaflet');
      
      // Remove existing chainage segment layer if it exists and is still on the map
      if (chainageSegmentLayer) {
        try {
          console.log('Removing existing chainage segment layer');
          mapInstance.removeLayer(chainageSegmentLayer);
        } catch (error) {
          console.warn('Error removing existing chainage segment layer (might already be removed):', error);
        }
        setChainageSegmentLayer(null);
      }
      
      // Create GeoJSON layer for the chainage segment
      const geoJsonLayer = L.geoJSON(chainageData.geometry, {
        style: {
          color: '#2196F3',
          weight: 3,
          opacity: 0.9,
          fillColor: '#2196F3',
          fillOpacity: 0.2,
          dashArray: '5, 10'
        },
        onEachFeature: (feature, layer) => {
          // Add popup with chainage segment info
          if (chainageData.name || chainageData.code || chainageData.km_marker) {
            const popupContent = `
              <div style="font-family: Arial, sans-serif;">
                <strong style="color: #2196F3;">Chainage Segment</strong><br/>
                ${chainageData.name ? `<strong>Name:</strong> ${chainageData.name}<br/>` : ''}
                ${chainageData.code ? `<strong>Code:</strong> ${chainageData.code}<br/>` : ''}
                ${chainageData.km_marker ? `<strong>KM:</strong> ${chainageData.km_marker}<br/>` : ''}
                <em style="color: #666; font-size: 0.9em;">ID: ${chainageData.id}</em>
              </div>
            `;
            layer.bindPopup(popupContent, {
              maxWidth: 250,
              className: 'chainage-segment-popup'
            });
          }
        }
      });
      
      // Add to map
      geoJsonLayer.addTo(mapInstance);
      setChainageSegmentLayer(geoJsonLayer);
      
      console.log('Chainage segment successfully added to map');
      
      // Try to fit the map to show both defect and chainage segment
      try {
        const defectLatLng = [
          imageData?.latitude || selectedDefect?.rawData?.latitude || 3.1390,
          imageData?.longitude || selectedDefect?.rawData?.longitude || 101.6869
        ];
        
        // Get bounds of chainage segment
        const chainageBounds = geoJsonLayer.getBounds();
        
        // Extend bounds to include defect location
        const defectLatLngObj = L.latLng(defectLatLng[0], defectLatLng[1]);
        chainageBounds.extend(defectLatLngObj);
        
        // Fit map to show both
        mapInstance.fitBounds(chainageBounds, { padding: [20, 20] });
        
        console.log('Map fitted to show both defect and chainage segment');
      } catch (boundsError) {
        console.warn('Error fitting map bounds:', boundsError);
      }
      
    } catch (error) {
      console.error('Error adding chainage segment to map:', error);
    }
  };

  const getRelativeCoordinates = (event, element) => {
    const rect = element.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const convertToImageCoordinates = (relativeCoords, imageElement) => {
    const rect = imageElement.getBoundingClientRect();
    const scaleX = (imageData?.image_width || 1920) / rect.width;
    const scaleY = (imageData?.image_height || 1080) / rect.height;
    
    return {
      x: relativeCoords.x * scaleX,
      y: relativeCoords.y * scaleY
    };
  };

  const handleMouseDown = (event, handle = null) => {
    if (!isEditing) return;
    
    event.preventDefault();
    event.stopPropagation(); // Prevent event bubbling
    
    const imageElement = imageRef.current;
    if (!imageElement) return;

    const relativeCoords = getRelativeCoordinates(event, imageElement);
    setDragStart(relativeCoords);

    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setIsDragging(false); // Ensure dragging is disabled when resizing
    } else {
      setIsDragging(true);
      setIsResizing(false); // Ensure resizing is disabled when dragging
    }
  };

  const handleMouseMove = (event) => {
    if (!isEditing || (!isDragging && !isResizing)) return;

    const imageElement = imageRef.current;
    if (!imageElement) return;

    const relativeCoords = getRelativeCoordinates(event, imageElement);
    const imageCoords = convertToImageCoordinates(relativeCoords, imageElement);

    if (isDragging) {
      // Move the entire box
      const startImageCoords = convertToImageCoordinates(dragStart, imageElement);
      const deltaX = imageCoords.x - startImageCoords.x;
      const deltaY = imageCoords.y - startImageCoords.y;
      
      setBoundingBox(prev => {
        const boxWidth = prev.x2 - prev.x1;
        const boxHeight = prev.y2 - prev.y1;
        
        let newX1 = prev.x1 + deltaX;
        let newY1 = prev.y1 + deltaY;
        
        // Keep within bounds
        newX1 = Math.max(0, Math.min(newX1, (imageData?.image_width || 1920) - boxWidth));
        newY1 = Math.max(0, Math.min(newY1, (imageData?.image_height || 1080) - boxHeight));
        
        return {
          x1: newX1,
          y1: newY1,
          x2: newX1 + boxWidth,
          y2: newY1 + boxHeight
        };
      });
      
      setDragStart(relativeCoords);
    } else if (isResizing && resizeHandle) {
      // Resize the box based on handle
      setBoundingBox(prev => {
        let newBox = { ...prev };
        const minSize = 20; // Minimum box size in image pixels
        
        switch (resizeHandle) {
          case 'nw': // Top-left
            newBox.x1 = Math.max(0, Math.min(imageCoords.x, prev.x2 - minSize));
            newBox.y1 = Math.max(0, Math.min(imageCoords.y, prev.y2 - minSize));
            break;
          case 'ne': // Top-right
            newBox.x2 = Math.min(imageData?.image_width || 1920, Math.max(imageCoords.x, prev.x1 + minSize));
            newBox.y1 = Math.max(0, Math.min(imageCoords.y, prev.y2 - minSize));
            break;
          case 'sw': // Bottom-left
            newBox.x1 = Math.max(0, Math.min(imageCoords.x, prev.x2 - minSize));
            newBox.y2 = Math.min(imageData?.image_height || 1080, Math.max(imageCoords.y, prev.y1 + minSize));
            break;
          case 'se': // Bottom-right
            newBox.x2 = Math.min(imageData?.image_width || 1920, Math.max(imageCoords.x, prev.x1 + minSize));
            newBox.y2 = Math.min(imageData?.image_height || 1080, Math.max(imageCoords.y, prev.y1 + minSize));
            break;
        }
        
        return newBox;
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // Zoom functions
  const zoomToBox = () => {
    const imageElement = imageRef.current;
    const containerElement = imageContainerRef.current;
    
    if (!imageElement || !containerElement || !boundingBox) return;
    
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();
    
    // Calculate bounding box center in image coordinates
    const boxCenterX = (boundingBox.x1 + boundingBox.x2) / 2;
    const boxCenterY = (boundingBox.y1 + boundingBox.y2) / 2;
    
    // Calculate bounding box dimensions
    const boxWidth = boundingBox.x2 - boundingBox.x1;
    const boxHeight = boundingBox.y2 - boundingBox.y1;
    
    // Add padding around the box (20% on each side)
    const paddingFactor = 0.4; // 20% padding on each side = 40% total
    const paddedBoxWidth = boxWidth * (1 + paddingFactor);
    const paddedBoxHeight = boxHeight * (1 + paddingFactor);
    
    // Calculate scale to fit the padded box in the container
    const scaleX = containerRect.width / (paddedBoxWidth / (imageData?.image_width || 1920) * imageRect.width);
    const scaleY = containerRect.height / (paddedBoxHeight / (imageData?.image_height || 1080) * imageRect.height);
    const targetScale = Math.min(scaleX, scaleY, 4); // Cap at 4x zoom
    
    // Calculate translation to center the box
    const imageCenterX = imageRect.width / 2;
    const imageCenterY = imageRect.height / 2;
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;
    
    // Convert box center to image element coordinates
    const boxCenterXInImage = (boxCenterX / (imageData?.image_width || 1920)) * imageRect.width;
    const boxCenterYInImage = (boxCenterY / (imageData?.image_height || 1080)) * imageRect.height;
    
    const translateX = containerCenterX - boxCenterXInImage * targetScale;
    const translateY = containerCenterY - boxCenterYInImage * targetScale;
    
    setZoom({
      scale: targetScale,
      translateX,
      translateY
    });
  };

  const resetZoom = () => {
    setZoom({ scale: 1, translateX: 0, translateY: 0 });
  };

  const toggleZoom = () => {
    if (zoom.scale === 1) {
      zoomToBox();
    } else {
      resetZoom();
    }
  };

  const handleZoom = (delta, centerX, centerY) => {
    const zoomFactor = delta > 0 ? 1.2 : 1 / 1.2;
    const newScale = Math.max(0.5, Math.min(zoom.scale * zoomFactor, 5));
    
    if (newScale === zoom.scale) return;
    
    // Calculate new translation to zoom towards the cursor position
    const scaleChange = newScale / zoom.scale;
    const newTranslateX = centerX - (centerX - zoom.translateX) * scaleChange;
    const newTranslateY = centerY - (centerY - zoom.translateY) * scaleChange;
    
    setZoom({
      scale: newScale,
      translateX: newTranslateX,
      translateY: newTranslateY
    });
  };

  const handleWheel = (event) => {
    if (!imageContainerRef.current) return;
    
    event.preventDefault();
    const rect = imageContainerRef.current.getBoundingClientRect();
    const centerX = event.clientX - rect.left;
    const centerY = event.clientY - rect.top;
    
    handleZoom(-event.deltaY, centerX, centerY);
  };

  const handlePanStart = (event) => {
    if (isEditing) return; // Don't pan while editing
    
    setIsPanning(true);
    setPanStart({
      x: event.clientX - zoom.translateX,
      y: event.clientY - zoom.translateY
    });
  };

  const handlePanMove = (event) => {
    if (!isPanning) return;
    
    setZoom(prev => ({
      ...prev,
      translateX: event.clientX - panStart.x,
      translateY: event.clientY - panStart.y
    }));
  };

  const handlePanEnd = () => {
    setIsPanning(false);
  };

  const cleanupMap = (reason = 'unknown') => {
    console.log('Cleaning up map, reason:', reason);
    let shouldCleanup = false;
    
    // Check if we have a map instance or container with Leaflet state
    if (mapInstance && !mapCleanedUp) {
      shouldCleanup = true;
    }
    
    if (mapContainerRef.current && mapContainerRef.current._leaflet_id) {
      shouldCleanup = true;
    }
    
    if (!shouldCleanup) {
      console.log('No cleanup needed');
      return;
    }
    
    // Remove chainage segment layer if it exists
    if (chainageSegmentLayer && mapInstance) {
      try {
        console.log('Removing chainage segment layer');
        mapInstance.removeLayer(chainageSegmentLayer);
      } catch (error) {
        console.warn('Error removing chainage segment layer:', error);
      }
    }
    
    // Remove map instance if it exists
    if (mapInstance) {
      try {
        // Check if map container still exists and is connected
        const container = mapInstance.getContainer();
        if (container && container.parentNode) {
          console.log('Removing map instance');
          mapInstance.remove();
        } else {
          console.log('Map container already disconnected, skipping removal');
        }
      } catch (error) {
        console.warn('Error during map cleanup:', error);
        // Continue with cleanup even if removal fails
      }
    }
    
    // Clean up container completely
    if (mapContainerRef.current) {
      const container = mapContainerRef.current;
      
      // Remove Leaflet's internal state
      if (container._leaflet_id) {
        delete container._leaflet_id;
      }
      
      // Remove all Leaflet-related classes
      container.className = container.className
        .split(' ')
        .filter(cls => !cls.startsWith('leaflet-'))
        .join(' ');
      
      // Reset all attributes and styles
      container.removeAttribute('tabindex');
      container.removeAttribute('style');
      container.innerHTML = '';
      container.style.cssText = '';
    }
    
    // Reset state - but preserve chainageSegmentData
    setMapInstance(null);
    setMapCleanedUp(true);
    setChainageSegmentLayer(null);
    console.log('Map cleanup complete, chainage data preserved:', !!chainageSegmentData);
    // Note: chainageSegmentData is intentionally NOT reset here
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      console.log(`Switching view from ${currentView} to ${newView}`);
      
      // Clean up map when switching away from map view
      if (currentView === 'map' && newView !== 'map') {
        console.log('Switching away from map, cleaning up...');
        cleanupMap('view change');
      }
      
      setCurrentView(newView);
      setIsEditing(false);
      
      // Initialize map when switching to map view
      if (newView === 'map') {
        console.log('Switching to map view, initializing map...');
        console.log('Chainage segment data available:', !!chainageSegmentData);
        setMapCleanedUp(false);
        setTimeout(() => {
          initializeMap();
        }, 200);
      }
    }
  };

  const initializeMap = async () => {
    if (!mapContainerRef.current) {
      console.log('Map container not available');
      return;
    }
    
    if (mapInstance) {
      console.log('Map instance already exists, skipping initialization');
      return;
    }
    
    setMapLoading(true);
    
    try {
      // Clear the container completely to ensure clean state
      if (mapContainerRef.current) {
        const container = mapContainerRef.current;
        
        // Check for any remaining Leaflet state
        if (container._leaflet_id) {
          console.log('Removing stale leaflet ID');
          delete container._leaflet_id;
        }
        
        // Remove any remaining Leaflet classes
        container.className = container.className
          .split(' ')
          .filter(cls => !cls.startsWith('leaflet-'))
          .join(' ');
        
        // Reset all attributes and styles
        container.removeAttribute('tabindex');
        container.removeAttribute('style');
        container.innerHTML = '';
        container.style.cssText = '';
      }
      
      // Dynamically import Leaflet
      const L = await import('leaflet');
      
      // Get coordinates from imageData
      const latitude = imageData?.latitude || selectedDefect?.rawData?.latitude || 3.1390; // Default to Kuala Lumpur
      const longitude = imageData?.longitude || selectedDefect?.rawData?.longitude || 101.6869;
      
      // Create map instance with fresh container
      const map = L.map(mapContainerRef.current, {
        center: [latitude, longitude],
        zoom: 16,
        zoomControl: true,
        preferCanvas: false,
        attributionControl: false,
      });
      
      // Try Stadia Maps first, with fallback to OpenStreetMap
      const stadiaLayer = L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png?api_key=948eb013-bbdc-42c1-98a5-6e6f0fede1d1', {
        maxZoom: 20,
        attribution: '',
      });
      
      // Fallback tile layer
      const osmLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: ''
      });
      
      // Add error handling for tile loading
      stadiaLayer.on('tileerror', (error) => {
        console.warn('Stadia Maps tile error, falling back to OSM:', error);
        map.removeLayer(stadiaLayer);
        osmLayer.addTo(map);
      });
      
      // Add the primary layer
      stadiaLayer.addTo(map);
      
      // Create custom defect icon
      const defectIcon = L.divIcon({
        className: 'defect-marker',
        html: `
          <div style="
            background-color: #f44336;
            border: 3px solid white;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            box-shadow: 0 2px 8px rgba(244, 67, 54, 0.4);
            cursor: pointer;
          "></div>
        `,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      
      // Add marker for defect location
      const marker = L.marker([latitude, longitude], { icon: defectIcon }).addTo(map);
      
      // Force map to refresh/invalidate size after container is ready
      setTimeout(() => {
        try {
          map.invalidateSize();
        } catch (error) {
          console.warn('Error invalidating map size:', error);
        }
      }, 200);
      
      // Store map instance AFTER everything is set up
      setMapInstance(map);
      
      // Add chainage segment if data is available (check both state and ref)
      const availableChainageData = chainageSegmentData || chainageSegmentDataRef.current;
      if (availableChainageData) {
        console.log('Map initialized, adding existing chainage segment data');
        // Use setTimeout to ensure map is fully ready before adding chainage segment
        setTimeout(() => {
          addChainageSegmentToMap(availableChainageData);
        }, 100);
      } else {
        console.log('Map initialized, no chainage segment data available yet');
      }
      
      // Add map click handler for future features
      map.on('click', (e) => {
        console.log('Map clicked at:', e.latlng);
      });
      
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapLoading(false);
    } finally {
      setTimeout(() => {
        setMapLoading(false);
      }, 500);
    }
  };

  // Cleanup map when component unmounts or modal closes
  useEffect(() => {
    return () => {
      if (mapInstance && !mapCleanedUp) {
        cleanupMap('component unmount');
      }
    };
  }, [mapInstance, mapCleanedUp]);

  // Cleanup map when modal closes
  useEffect(() => {
    if (!open && mapInstance && !mapCleanedUp) {
      cleanupMap('modal close');
    }
  }, [open, mapInstance, mapCleanedUp]);

  // Add chainage segment to map when data becomes available
  useEffect(() => {
    const availableChainageData = chainageSegmentData || chainageSegmentDataRef.current;
    if (mapInstance && availableChainageData && availableChainageData.geometry) {
      console.log('Chainage segment data available, adding to map');
      // Always try to add chainage segment when map is available and data exists
      // The addChainageSegmentToMap function will handle removing existing layers
      addChainageSegmentToMap(availableChainageData);
    }
  }, [mapInstance, chainageSegmentData]);

  // Full-size image viewer handlers
  const handleBurstImageClick = (index) => {
    setCurrentFullSizeIndex(index);
    setFullSizeImageOpen(true);
  };

  const handleFullSizeClose = () => {
    setFullSizeImageOpen(false);
  };

  const handleFullSizeNext = () => {
    setCurrentFullSizeIndex((prev) => (prev + 1) % burstFindings.length);
  };

  const handleFullSizePrev = () => {
    setCurrentFullSizeIndex((prev) => (prev - 1 + burstFindings.length) % burstFindings.length);
  };

  const handleFullSizeKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      handleFullSizeNext();
    } else if (event.key === 'ArrowLeft') {
      handleFullSizePrev();
    } else if (event.key === 'Escape') {
      handleFullSizeClose();
    }
  };

  const renderMapContent = () => {
    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%', 
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Map Loading State */}
        {(mapLoading || loadingChainageSegment) && (
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'rgba(255,255,255,0.9)',
            p: 2,
            borderRadius: 2
          }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {loadingChainageSegment ? 'Loading chainage segment...' : 'Loading map...'}
            </Typography>
          </Box>
        )}
        
        {/* Map Container */}
        <Box
          ref={mapContainerRef}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '400px',
            maxHeight: '600px',
            borderRadius: 1,
            overflow: 'hidden',
            backgroundColor: '#f5f5f5',
            position: 'relative',
            '& .leaflet-container': {
              height: '100% !important',
              width: '100% !important',
              borderRadius: 1,
            },
            '& .leaflet-popup-content-wrapper': {
              borderRadius: '8px',
            },
            '& .leaflet-popup-tip': {
              backgroundColor: 'white',
            },
            '& .leaflet-control-zoom': {
              borderRadius: '6px',
              overflow: 'hidden',
            },
            '& .chainage-segment-popup': {
              '& .leaflet-popup-content-wrapper': {
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }
            }
          }}
        />
        
        {/* Map Legend */}
        {!mapLoading && chainageSegmentData && (
          <Box sx={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            bgcolor: 'rgba(255,255,255,0.95)',
            p: 1.5,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: '1px solid rgba(0,0,0,0.1)',
            fontSize: '0.75rem',
            zIndex: 1000
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
              Legend
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Box sx={{ 
                width: 12, 
                height: 12, 
                borderRadius: '50%', 
                bgcolor: '#f44336',
                border: '2px solid white',
                boxShadow: '0 1px 3px rgba(244, 67, 54, 0.4)'
              }} />
              <Typography variant="caption">Defect Location</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ 
                width: 12, 
                height: 8, 
                border: '1px dashed #2196F3',
                bgcolor: 'rgba(33, 150, 243, 0.1)'
              }} />
              <Typography variant="caption">Chainage Segment</Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  if (!selectedDefect) return null;

  const renderImageContent = () => {
    if (!imageData?.original_image_path || imageData.original_image_path === 'no-image') {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: 'text.secondary'
        }}>
          <Typography variant="h4" sx={{ mb: 1, opacity: 0.3 }}>📷</Typography>
          <Typography variant="body2">No image available</Typography>
        </Box>
      );
    }

    if (imageError) {
      return (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          color: 'text.secondary',
          textAlign: 'center',
        }}>
          <Typography variant="h4" sx={{ mb: 1, opacity: 0.3 }}>📷</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Image not available</Typography>
          <Typography variant="caption">Path: {imageData.original_image_path}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f8f9fa',
        cursor: (isEditing && !viewOnly) ? 'crosshair' : (isPanning ? 'grabbing' : (zoom.scale > 1 ? 'grab' : 'default')),
        overflow: 'hidden'
      }}
      ref={imageContainerRef}
      onWheel={handleWheel}
      onMouseDown={(e) => {
        if (!isEditing && !viewOnly && zoom.scale > 1) {
          handlePanStart(e);
        }
      }}
      >
        {imageLoading && (
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 2
          }}>
            <CircularProgress size={32} />
          </Box>
        )}
        
        <Box sx={{
          transform: `scale(${zoom.scale}) translate(${zoom.translateX / zoom.scale}px, ${zoom.translateY / zoom.scale}px)`,
          transformOrigin: '0 0',
          transition: isPanning || isDragging || isResizing ? 'none' : 'transform 0.3s ease-out'
        }}>
          <AuthenticatedImage
            ref={imageRef}
            src={mainImageUrl}
            alt="Defect detection"
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: imageLoading ? 'none' : 'block',
              userSelect: 'none'
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
          />
          
          {!imageLoading && !imageError && imageData && imageRef.current && (
            <Box
              sx={{
                position: 'absolute',
                border: `2px ${viewOnly ? 'solid' : 'dashed'} ${isEditing ? theme.palette.warning.main : theme.palette.error.main}`,
                borderRadius: '4px',
                backgroundColor: alpha(isEditing ? theme.palette.warning.main : theme.palette.error.main, 0.1),
                pointerEvents: (isEditing && !viewOnly) ? 'auto' : 'none',
                cursor: (isEditing && !viewOnly) ? (isDragging ? 'grabbing' : 'grab') : 'default',
                left: `${(boundingBox.x1 / (imageData?.image_width || 1920)) * 100}%`,
                top: `${(boundingBox.y1 / (imageData?.image_height || 1080)) * 100}%`,
                width: `${((boundingBox.x2 - boundingBox.x1) / (imageData?.image_width || 1920)) * 100}%`,
                height: `${((boundingBox.y2 - boundingBox.y1) / (imageData?.image_height || 1080)) * 100}%`,
                ...(shouldBlink && {
                  animation: 'boundingBoxBlink 1s ease-in-out 3',
                  '@keyframes boundingBoxBlink': {
                    '0%': { 
                      opacity: 1,
                      borderColor: isEditing ? theme.palette.warning.main : theme.palette.error.main,
                      backgroundColor: alpha(isEditing ? theme.palette.warning.main : theme.palette.error.main, 0.1)
                    },
                    '50%': { 
                      opacity: 0.3,
                      borderColor: isEditing ? theme.palette.warning.light : theme.palette.error.light,
                      backgroundColor: alpha(isEditing ? theme.palette.warning.main : theme.palette.error.main, 0.3)
                    },
                    '100%': { 
                      opacity: 1,
                      borderColor: isEditing ? theme.palette.warning.main : theme.palette.error.main,
                      backgroundColor: alpha(isEditing ? theme.palette.warning.main : theme.palette.error.main, 0.1)
                    }
                  }
                })
              }}
              onMouseDown={(e) => !viewOnly && handleMouseDown(e)}
            >
              {isEditing && !viewOnly && (
                <>
                  {/* Resize handles */}
                  {['nw', 'ne', 'sw', 'se'].map((handle) => (
                    <Box
                      key={handle}
                      sx={{
                        position: 'absolute',
                        width: 10,
                        height: 10,
                        bgcolor: theme.palette.warning.main,
                        border: `2px solid white`,
                        borderRadius: '50%',
                        cursor: `${handle}-resize`,
                        zIndex: 10,
                        ...(handle === 'nw' && { top: -5, left: -5 }),
                        ...(handle === 'ne' && { top: -5, right: -5 }),
                        ...(handle === 'sw' && { bottom: -5, left: -5 }),
                        ...(handle === 'se' && { bottom: -5, right: -5 }),
                        '&:hover': {
                          bgcolor: theme.palette.warning.dark,
                          transform: 'scale(1.2)'
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleMouseDown(e, handle);
                      }}
                    />
                  ))}
                  
                  {/* Center indicator for dragging */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 16,
                      height: 16,
                      bgcolor: alpha(theme.palette.warning.main, 0.8),
                      border: `1px solid white`,
                      borderRadius: '50%',
                      cursor: 'grab',
                      '&:hover': {
                        bgcolor: theme.palette.warning.main,
                      }
                    }}
                  />
                </>
              )}
            </Box>
          )}
        </Box>

        {/* AI Confidence Badge - Bottom Left */}
        {!imageLoading && !imageError && (
          <Box sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            bgcolor: 'rgba(0, 0, 0, 0.75)',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: '6px',
            fontSize: '0.8rem',
            fontWeight: 600,
            zIndex: 10
          }}>
            AI: {((imageData?.confidence_score || selectedDefect.confidence) * 100).toFixed(0)}%
          </Box>
        )}

        {/* Defect Type Badge - Bottom Right */}
        {!imageLoading && !imageError && (
          <Chip
            label={imageData?.defect_type_name || 'Unknown'}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem',
              height: 28,
              zIndex: 10
            }}
          />
        )}

        {/* Zoom Controls */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          zIndex: 10
        }}>
          <Button
            variant="contained"
            size="small"
            onClick={toggleZoom}
            startIcon={zoom.scale === 1 ? <ZoomInIcon fontSize="small" /> : <ZoomOutMapIcon fontSize="small" />}
            sx={{ 
              minWidth: 'auto',
              px: 1.5,
              fontSize: '0.75rem',
              bgcolor: zoom.scale === 1 ? 'rgba(255,255,255,0.9)' : 'rgba(33, 150, 243, 0.9)',
              color: zoom.scale === 1 ? theme.palette.primary.main : 'white',
              border: zoom.scale === 1 ? `1px solid ${theme.palette.primary.main}` : 'none',
              '&:hover': {
                bgcolor: zoom.scale === 1 ? 'rgba(255,255,255,1)' : 'rgba(33, 150, 243, 1)'
              }
            }}
          >
            {zoom.scale === 1 ? 'Zoom to Box' : 'Reset View'}
          </Button>
          
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            textAlign: 'center'
          }}>
            {Math.round(zoom.scale * 100)}%
          </Box>
        </Box>

        {/* Edit Toggle Button */}
        <Box sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 1,
          zIndex: 10
        }}>
          {!viewOnly && (
            <Button
              variant={isEditing ? "contained" : "outlined"}
              size="small"
              color={isEditing ? "warning" : "primary"}
              onClick={() => setIsEditing(!isEditing)}
              sx={{ 
                minWidth: 'auto',
                px: 1.5,
                fontSize: '0.75rem',
                bgcolor: isEditing ? theme.palette.warning.main : 'rgba(255,255,255,0.9)',
                color: isEditing ? 'white' : theme.palette.primary.main,
                '&:hover': {
                  bgcolor: isEditing ? theme.palette.warning.dark : 'rgba(255,255,255,1)'
                }
              }}
            >
              {isEditing ? 'Done' : 'Edit Box'}
            </Button>
          )}
        </Box>

        {/* Help text when zoomed - auto-hides after 3 seconds */}
        {showZoomHint && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: '0.75rem',
            textAlign: 'center',
            pointerEvents: 'none',
            opacity: isPanning ? 0 : 0.8,
            transition: 'opacity 0.3s ease'
          }}>
            Drag to pan • Scroll to zoom
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: theme.shadows[16],
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      {/* COMPACT HEADER */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 1.5,
        px: 2.5,
        flexShrink: 0,
        bgcolor: 'background.paper'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VisibilityIcon color="primary" fontSize="small" /> 
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {viewOnly ? 'View Detection' : 'Validate Detection'}
          </Typography>
          <Chip 
            label={`ID: ${selectedDefect.recordType === 'confirmed_defect' ? `D-${selectedDefect.defectId}` : `F-${selectedDefect.id}`}`}
            icon={selectedDefect.recordType === 'confirmed_defect' ? 
              <BugReportIcon fontSize="small" /> : 
              <HelpIcon fontSize="small" />
            }
            size="small" 
            variant="outlined"
            sx={{ ml: 1, fontSize: '0.7rem' }}
          />
          {viewOnly && (
            <Chip 
              label="View Only" 
              size="small" 
              color="info" 
              variant="outlined"
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          )}
          {showBurstSection && burstStatistics && (
            <Chip 
              label={`Burst: ${burstStatistics.total_detections} images`} 
              size="small" 
              color="warning" 
              variant="outlined"
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {/* DYNAMIC CONTENT AREA */}
      <DialogContent sx={{ 
        p: 0, 
        display: 'flex',
        overflow: 'hidden',
        minHeight: '400px',
        maxHeight: 'calc(90vh - 120px)',
      }}>
        <Box sx={{ display: 'flex', width: '100%' }}>
          
          {/* LEFT SIDE - IMAGE CANVAS (DOMINANT) */}
          <Box sx={{ 
            width: '65%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Compact Image Title */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                {currentView === 'image' ? 'AI Detection Image' : 'Location Map'}
              </Typography>
              
              {/* View Toggle */}
              <ToggleButtonGroup
                value={currentView}
                exclusive
                onChange={handleViewChange}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[100], 0.8),
                  borderRadius: '8px',
                  p: 0.25,
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 0.75,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    border: 'none',
                    borderRadius: '6px',
                    color: theme.palette.text.secondary,
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                    '&.Mui-selected': {
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="image" aria-label="image view">
                  <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Image
                </ToggleButton>
                <ToggleButton value="map" aria-label="map view">
                  <MapIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Map
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Image Area - Dynamic Height */}
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              minHeight: '300px',
              maxHeight: '600px',
              aspectRatio: imageData?.image_width && imageData?.image_height 
                ? `${imageData.image_width} / ${imageData.image_height}` 
                : '16 / 9'
            }}>
              {currentView === 'image' ? renderImageContent() : renderMapContent()}
            </Box>
            
            {/* Compact Image Footer */}
            <Box sx={{ 
              px: 3, 
              py: 1.5,
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              bgcolor: alpha(theme.palette.grey[50], 0.5),
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                {currentView === 'image' ? (
                  <>
                    {formatDateTime(selectedDefect.detectedAt)} • {selectedDefect.location}
                    {imageData?.image_width && imageData?.image_height && (
                      <> • {imageData.image_width} × {imageData.image_height} px</>
                    )}
                    {imageData?.latitude && imageData?.longitude && (
                      <> • Lat: {imageData.latitude.toFixed(6)}, Lng: {imageData.longitude.toFixed(6)}</>
                    )}
                  </>
                ) : (
                  <>
                    {formatDateTime(selectedDefect.detectedAt)} • 
                    {imageData?.latitude && imageData?.longitude && (
                      <> Lat: {imageData.latitude.toFixed(6)}, Lng: {imageData.longitude.toFixed(6)}</>
                    )}
                    {selectedDefect?.location && (
                      <> • {selectedDefect.location}</>
                    )}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
          
          {/* RIGHT SIDE - COMPACT DETAILS */}
          <Box sx={{ 
            width: '35%',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
              
              {/* Validation Details Section */}
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                {viewOnly ? 'Detection Details' : 'Validation Details'}
              </Typography>
              
              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Detection Type</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {imageData?.defect_type_name || 'Unknown'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Detected By</Typography>
                  <Typography variant="body2" fontWeight={600}>AI Camera</Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Location</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', maxWidth: '60%', fontSize: '0.8rem', lineHeight: 1.3 }}>
                    {selectedDefect.location}
                  </Typography>
                </Box>

                {/* Show dimensions for potholes and foreign objects */}
                {(() => {
                  const defectTypeName = (imageData?.defect_type_name || imageData?.validated_defect_type_name || selectedDefect?.defectType || '').toLowerCase();
                  const isPothole = defectTypeName.includes('pothole');
                  const isForeignObject = defectTypeName.includes('foreign object') || defectTypeName.includes('road debris');
                  const showMeasurements = isPothole || isForeignObject;
                  const lengthCm = imageData?.height_cm || selectedDefect?.rawData?.height_cm;
                  const widthCm = imageData?.width_cm || selectedDefect?.rawData?.width_cm;
                  const hasRealLength = lengthCm && lengthCm > 0.5;
                  const hasRealWidth = widthCm && widthCm > 0.5;

                  if (showMeasurements && (hasRealLength || hasRealWidth)) {
                    const measurementLabel = isPothole ? 'Pothole' : 'Object';
                    return (
                      <>
                        <Box sx={{
                          mb: 1.5,
                          p: 1,
                          bgcolor: alpha(theme.palette.warning.main, 0.05),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
                        }}>
                          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                            📏 {measurementLabel} Measurements
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            {hasRealLength && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Length:</Typography>
                                <Typography variant="caption" fontWeight={600}>{Math.round(lengthCm)}cm</Typography>
                              </Box>
                            )}
                            {hasRealWidth && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Width:</Typography>
                                <Typography variant="caption" fontWeight={600}>{Math.round(widthCm)}cm</Typography>
                              </Box>
                            )}
                          </Box>
                          {hasRealLength && hasRealWidth && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontSize: '0.65rem' }}>
                              Area: ~{Math.round(lengthCm * widthCm)} cm²
                            </Typography>
                          )}
                        </Box>
                      </>
                    );
                  }
                  return null;
                })()}

                {viewOnly && selectedDefect.validatedBy && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Validated By</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedDefect.validatedBy}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Validated At</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDateTime(selectedDefect.validatedAt)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Correct Classification Section or Validated Classification */}
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                {viewOnly ? 'Validated Classification' : 'Correct Classification'}
              </Typography>
              
              {loadingDefectTypes ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <>
                  <FormControl fullWidth sx={{ mb: 2 }} size="small">
                    <InputLabel>Defect Type</InputLabel>
                    <Select
                      value={currentDefectTypeId}
                      label="Defect Type"
                      onChange={onDefectTypeChange}
                      disabled={viewOnly}
                    >
                      {defectTypesList.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Typography variant="body2">{type.name}</Typography>
                            {imageData?.defect_type_id === type.id && (
                              <Chip 
                                label="Original" 
                                size="small" 
                                color="primary" 
                                variant="outlined" 
                                sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }} 
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={viewOnly ? "Validation Notes" : "Validation Notes"}
                    value={rejectionReason}
                    onChange={onRejectionReasonChange}
                    placeholder={viewOnly ? "No validation notes" : "Add validation notes..."}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2 }}
                    disabled={viewOnly}
                    InputProps={{
                      readOnly: viewOnly,
                    }}
                  />

                  {/* Correct Bound Section */}
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                    Correct Bound
                  </Typography>
                  
                  {loadingNearbySegments ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <FormControl fullWidth sx={{ mb: 2 }} size="small">
                      <InputLabel>Chainage Segment</InputLabel>
                      <Select
                        value={selectedChainageSegmentId}
                        label="Chainage Segment"
                        onChange={(e) => setSelectedChainageSegmentId(e.target.value)}
                        disabled={viewOnly || (!selectedDefect?.rawData?.matched_chainage_segment_id && nearbyChainageSegments.length === 0)}
                      >
                        {/* Add the original segment as the first option */}
                        {selectedDefect?.rawData?.matched_chainage_segment_id && (
                          <MenuItem value={selectedDefect.rawData.matched_chainage_segment_id.toString()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2">{selectedDefect.rawData.chainage_segment_name}</Typography>
                              <Chip
                                label="Original"
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ ml: 'auto', height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </MenuItem>
                        )}
                        {nearbyChainageSegments.map((segment) => (
                          <MenuItem key={segment.id} value={segment.id.toString()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography variant="body2">{segment.name}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                {Math.round(segment.distance)}m away
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                        
                        {/* Show message when no nearby segments found */}
                        {!loadingNearbySegments && nearbyChainageSegments.length === 0 && !selectedDefect?.rawData?.matched_chainage_segment_id && (
                          <MenuItem disabled>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No chainage segments found
                            </Typography>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  )}

                  {/* Compact Validator Info */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 'auto',
                    pt: 1,
                    color: 'text.secondary'
                  }}>
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      borderRadius: '50%', 
                      bgcolor: theme.palette.grey[300],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem'
                    }}>
                      👤
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                      {viewOnly ? 'Viewing validated detection' : `You are validating as ${user?.fullName || 'Unknown User'}`}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Burst Images Section */}
              {showBurstSection && (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 1.5, mt: 2, fontWeight: 600, color: 'text.primary' }}>
                    Burst Images {loadingBurstFindings && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </Typography>
                  
                  {burstStatistics && (
                    <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
                      <Typography variant="caption" color="info.main" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Burst Detection Summary
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                          label={`${burstStatistics.total_detections} images`} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Chip 
                          label={`${burstStatistics.duration_seconds}s span`} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                        <Chip 
                          label={`${(burstStatistics.avg_confidence * 100).toFixed(0)}% avg conf`} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', 
                    gap: 1, 
                    maxHeight: '200px', 
                    overflowY: 'auto',
                    pr: 0.5
                  }}>
                    {burstFindings.map((finding, index) => {
                      const imageUrl = burstImageUrls[index];
                      
                      // Show placeholder if no image path
                      if (!finding.image_path || finding.image_path === 'no-image') {
                        if (process.env.NODE_ENV === 'development') {
                          console.warn(`⚠️ [Burst Image ${index}] No valid image path found for burst finding ${finding.id}`);
                        }
                        return (
                          <Box 
                            key={finding.id} 
                            sx={{ 
                              position: 'relative',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: `2px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                              height: '60px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(theme.palette.grey[200], 0.3),
                              flexDirection: 'column'
                            }}
                          >
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                              No Image
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.5rem', opacity: 0.7 }}>
                              #{finding.burst_sequence_order}
                            </Typography>
                          </Box>
                        );
                      }
                      
                      return (
                        <Box 
                          key={finding.id} 
                          sx={{ 
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={() => handleBurstImageClick(index)}
                        >
                          <AuthenticatedImage
                            src={imageUrl}
                            alt={`Burst ${finding.burst_sequence_order}`}
                            sx={{
                              width: '100%',
                              height: '60px',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                          <Box sx={{
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontSize: '0.6rem',
                            fontWeight: 600
                          }}>
                            #{finding.burst_sequence_order}
                          </Box>
                          <Box sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            bgcolor: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            px: 0.5,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontSize: '0.6rem'
                          }}>
                            +{Math.round(finding.time_from_first_detection_seconds * 10) / 10}s
                          </Box>
                          {/* Fallback for broken images */}
                          <Box sx={{
                            display: 'none',
                            width: '100%',
                            height: '60px',
                            bgcolor: alpha(theme.palette.grey[300], 0.3),
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            flexDirection: 'column'
                          }}>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', mb: 0.5 }}>
                              Image Error
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.5rem', opacity: 0.7 }}>
                              #{finding.burst_sequence_order}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem', textAlign: 'center' }}>
                    Click any image to view full size
                  </Typography>
                </>
              )}

              {/* Auto-Assign Preview Section - Inside RIGHT column */}
              {!viewOnly && autoAssignPreview && (
                <Box sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: autoAssignPreview.auto_assign_enabled && autoAssignPreview.will_auto_assign
                    ? alpha(theme.palette.success.main, 0.08)
                    : autoAssignPreview.auto_assign_enabled && !autoAssignPreview.will_auto_assign
                      ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(theme.palette.grey[500], 0.08),
                  border: `1px solid ${
                    autoAssignPreview.auto_assign_enabled && autoAssignPreview.will_auto_assign
                      ? alpha(theme.palette.success.main, 0.3)
                      : autoAssignPreview.auto_assign_enabled && !autoAssignPreview.will_auto_assign
                        ? alpha(theme.palette.warning.main, 0.3)
                        : alpha(theme.palette.grey[500], 0.3)
                  }`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <AssignmentIcon
                      sx={{ fontSize: '1rem' }}
                      color={
                        autoAssignPreview.auto_assign_enabled && autoAssignPreview.will_auto_assign
                          ? 'success'
                          : autoAssignPreview.auto_assign_enabled && !autoAssignPreview.will_auto_assign
                            ? 'warning'
                            : 'disabled'
                      }
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: autoAssignPreview.auto_assign_enabled && autoAssignPreview.will_auto_assign
                          ? 'success.dark'
                          : autoAssignPreview.auto_assign_enabled && !autoAssignPreview.will_auto_assign
                            ? 'warning.dark'
                            : 'text.secondary'
                      }}
                    >
                      {autoAssignPreview.auto_assign_enabled
                        ? (autoAssignPreview.will_auto_assign ? 'Auto-Assign Active' : 'Auto-Assign Warning')
                        : 'Auto-Assign Disabled'}
                    </Typography>
                  </Box>

                  {autoAssignPreview.auto_assign_enabled && autoAssignPreview.will_auto_assign && (
                    <>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        Work order will be assigned to:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {autoAssignPreview.assignable_users?.map((user) => (
                          <Chip
                            key={user.id}
                            icon={<PersonIcon sx={{ fontSize: '0.9rem !important' }} />}
                            label={user.full_name}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.5 } }}
                          />
                        ))}
                      </Box>
                    </>
                  )}

                  {autoAssignPreview.auto_assign_enabled && !autoAssignPreview.will_auto_assign && (
                    <Typography variant="caption" color="warning.dark">
                      {autoAssignPreview.message || 'No network/section users found.'}
                    </Typography>
                  )}

                  {!autoAssignPreview.auto_assign_enabled && (
                    <Typography variant="caption" color="text.secondary">
                      Disabled. Create work order manually after validation.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Loading state for auto-assign preview */}
              {!viewOnly && loadingAutoAssignPreview && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">
                    Checking auto-assign...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* COMPACT FOOTER */}
      <DialogActions sx={{
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        justifyContent: (viewOnly && !canRevert) ? 'flex-end' : 'space-between',
        flexShrink: 0,
        bgcolor: 'background.paper'
      }}>
        {!viewOnly && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DoNotDisturbAltIcon />}
            onClick={onReject}
            disabled={loadingDefectTypes}
            size="medium"
          >
            Reject Detection
          </Button>
        )}

        {/* Revert to Draft button - only shown for validated findings with permission */}
        {viewOnly && canRevert && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            onClick={() => setRevertDialogOpen(true)}
            disabled={reverting}
            size="medium"
          >
            Revert to Draft
          </Button>
        )}

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={onClose}
            color="inherit"
            size="medium"
          >
            {viewOnly ? 'Close' : 'Cancel'}
          </Button>
          {!viewOnly && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={() => setConfirmOpen(true)}
              disabled={loadingDefectTypes || !currentDefectTypeId}
              size="medium"
            >
              Confirm Defect
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>

    {/* Full-size image viewer modal */}
    <Dialog
      open={fullSizeImageOpen}
      onClose={handleFullSizeClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          color: 'white'
        }
      }}
      onKeyDown={handleFullSizeKeyDown}
    >
      <Box sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}>
        {/* Close button */}
        <IconButton
          onClick={handleFullSizeClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.7)'
            },
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Navigation buttons */}
        {burstFindings.length > 1 && (
          <>
            <IconButton
              onClick={handleFullSizePrev}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                zIndex: 1
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>

            <IconButton
              onClick={handleFullSizeNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                zIndex: 1
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </>
        )}

        {/* Image counter */}
        <Box sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          px: 2,
          py: 1,
          borderRadius: 1,
          zIndex: 1
        }}>
          <Typography variant="body2">
            {currentFullSizeIndex + 1} of {burstFindings.length}
          </Typography>
        </Box>

        {/* Image info */}
        {burstFindings[currentFullSizeIndex] && (
          <Box sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            px: 2,
            py: 1,
            borderRadius: 1,
            maxWidth: '300px',
            zIndex: 1
          }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Burst #{burstFindings[currentFullSizeIndex].burst_sequence_order}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              +{Math.round(burstFindings[currentFullSizeIndex].time_from_first_detection_seconds * 10) / 10}s from detection
            </Typography>
          </Box>
        )}

        {/* Keyboard shortcuts info */}
        <Box sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          px: 2,
          py: 1,
          borderRadius: 1,
          zIndex: 1
        }}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            ← → Navigate • ESC Close
          </Typography>
        </Box>

        {/* Full-size image with bounding box */}
        {burstFindings[currentFullSizeIndex] && (
          <Box sx={{ 
            position: 'relative', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}>
            <AuthenticatedImage
              src={burstImageUrls[currentFullSizeIndex]}
              alt={`Burst ${burstFindings[currentFullSizeIndex].burst_sequence_order} full size`}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
                display: 'block'
              }}
            />
            
            {/* Bounding box overlay - use coordinates from current burst finding */}
            {(() => {
              const currentBurst = burstFindings[currentFullSizeIndex];
              const hasValidCoordinates = currentBurst && 
                currentBurst.x1 !== undefined && 
                currentBurst.y1 !== undefined && 
                currentBurst.x2 !== undefined && 
                currentBurst.y2 !== undefined;
              
              // Use burst finding's own image dimensions if available, fallback to imageData
              const imgWidth = currentBurst?.image_width || imageData?.image_width || 1920;
              const imgHeight = currentBurst?.image_height || imageData?.image_height || 1080;
              
              if (!hasValidCoordinates) {
                console.log(`⚠️ [Full Screen Box] No valid coordinates for burst ${currentFullSizeIndex}:`, {
                  x1: currentBurst?.x1,
                  y1: currentBurst?.y1, 
                  x2: currentBurst?.x2,
                  y2: currentBurst?.y2
                });
                return null;
              }
              
              console.log(`📦 [Full Screen Box] Showing bounding box for burst ${currentFullSizeIndex}:`, {
                coordinates: {
                  x1: currentBurst.x1,
                  y1: currentBurst.y1,
                  x2: currentBurst.x2,
                  y2: currentBurst.y2
                },
                imageDimensions: { width: imgWidth, height: imgHeight },
                sequence: currentBurst.burst_sequence_order
              });
              
              return (
                <Box
                  sx={{
                    position: 'absolute',
                    border: '3px solid #ff6b6b',
                    borderRadius: '4px',
                    backgroundColor: 'rgba(255, 107, 107, 0.15)',
                    pointerEvents: 'none',
                    // Calculate position based on current burst image dimensions
                    left: `${(currentBurst.x1 / imgWidth) * 100}%`,
                    top: `${(currentBurst.y1 / imgHeight) * 100}%`,
                    width: `${((currentBurst.x2 - currentBurst.x1) / imgWidth) * 100}%`,
                    height: `${((currentBurst.y2 - currentBurst.y1) / imgHeight) * 100}%`,
                    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.5), 0 0 10px rgba(255, 107, 107, 0.6)',
                    // Add animation
                    animation: 'boundingBoxPulse 2s ease-in-out infinite',
                    '@keyframes boundingBoxPulse': {
                      '0%': { 
                        opacity: 0.8,
                        borderColor: '#ff6b6b',
                        backgroundColor: 'rgba(255, 107, 107, 0.15)'
                      },
                      '50%': { 
                        opacity: 1,
                        borderColor: '#ff4757',
                        backgroundColor: 'rgba(255, 107, 107, 0.25)'
                      },
                      '100%': { 
                        opacity: 0.8,
                        borderColor: '#ff6b6b',
                        backgroundColor: 'rgba(255, 107, 107, 0.15)'
                      }
                    }
                  }}
                >
                  {/* Bounding box label with sequence info */}
                  <Box sx={{
                    position: 'absolute',
                    top: -30,
                    left: 0,
                    bgcolor: 'rgba(255, 107, 107, 0.9)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                  }}>
                    Defect #{currentBurst.burst_sequence_order}
                  </Box>
                </Box>
              );
            })()}
          </Box>
        )}
      </Box>
    </Dialog>

    {/* Confirmation Dialog */}
    <Dialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minWidth: '400px'
        }
      }}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1
        }}
      >
        <CheckCircleIcon color="primary" />
        Confirm Validation
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description" sx={{ mb: 2 }}>
          Are you sure you want to validate this finding?
        </DialogContentText>
        
        <Box sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 2,
          p: 2,
          mt: 2
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
            Validation Summary:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Finding ID:</strong> F-{selectedDefect?.id}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Original Type:</strong> {selectedDefect?.defectType}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>New Type:</strong> {defectTypesList.find(t => t.id === parseInt(currentDefectTypeId))?.name || 'N/A'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Original Bound:</strong> {selectedDefect?.rawData?.chainage_segment_name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Corrected Bound:</strong> {
              selectedChainageSegmentId && selectedChainageSegmentId !== (selectedDefect?.rawData?.matched_chainage_segment_id || '').toString() 
                ? nearbyChainageSegments.find(seg => seg.id.toString() === selectedChainageSegmentId)?.name || 'N/A'
                : 'No change'
            }
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => setConfirmOpen(false)}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            const hasCoordinateChanges = boundingBox.x1 !== imageData?.x1 ||
                                        boundingBox.y1 !== imageData?.y1 ||
                                        boundingBox.x2 !== imageData?.x2 ||
                                        boundingBox.y2 !== imageData?.y2;
            
            const hasChainageSegmentChange = selectedChainageSegmentId && 
                                           selectedChainageSegmentId !== (selectedDefect?.rawData?.matched_chainage_segment_id || '').toString();
            
            // Always call onValidate - it handles the actual defect type validation
            const validationData = {};
            
            // Include coordinate changes if any
            if (hasCoordinateChanges) {
              validationData.adjustedBoundingBox = boundingBox;
              validationData.coordinatesChanged = true;
            }
            
            // Include chainage segment change if any
            if (hasChainageSegmentChange) {
              validationData.matched_chainage_segment_id = parseInt(selectedChainageSegmentId, 10);
            }
            
            onValidate(validationData);
            setConfirmOpen(false);
          }}
          variant="contained"
          color="primary"
          startIcon={<CheckIcon />}
          sx={{ minWidth: '120px' }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>

    {/* Revert to Draft Confirmation Dialog */}
    <Dialog
      open={revertDialogOpen}
      onClose={() => !reverting && setRevertDialogOpen(false)}
      aria-labelledby="revert-dialog-title"
      aria-describedby="revert-dialog-description"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minWidth: '400px'
        }
      }}
    >
      <DialogTitle
        id="revert-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1
        }}
      >
        <WarningIcon color="warning" />
        Revert to Draft
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="revert-dialog-description" sx={{ mb: 2 }}>
          Are you sure you want to revert this validated finding back to draft status?
        </DialogContentText>

        <Box sx={{
          bgcolor: alpha(theme.palette.warning.main, 0.08),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          borderRadius: 2,
          p: 2,
          mt: 2
        }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'warning.dark' }}>
            This action will:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Delete the confirmed defect record {selectedDefect?.defectId ? `(D-${selectedDefect.defectId})` : ''}
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Reset the finding status to "Draft"
            </Typography>
            <Typography component="li" variant="body2">
              Clear all validation details
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: 'error.main' }}>
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => setRevertDialogOpen(false)}
          variant="outlined"
          disabled={reverting}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            setReverting(true);
            try {
              await onRevertToDraft();
              setRevertDialogOpen(false);
            } catch (error) {
              console.error('Error reverting to draft:', error);
            } finally {
              setReverting(false);
            }
          }}
          variant="contained"
          color="warning"
          startIcon={reverting ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
          disabled={reverting}
          sx={{ minWidth: '140px' }}
        >
          {reverting ? 'Reverting...' : 'Confirm Revert'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default DefectValidationModal;